<template>
  <Teleport to="body">
    <v-navigation-drawer
      :model-value="modelValue"
      location="right"
      temporary
      :width="drawerWidth"
      class="bq-inquiry-drawer"
      @update:model-value="onDrawerUpdate"
    >
      <div v-if="query" class="bq-inquiry-drawer__inner">
        <header class="bq-inquiry-header">
          <div class="bq-inquiry-header__lead">
            <span class="bq-inquiry-header__avatar" aria-hidden="true">{{ initials }}</span>
            <div class="bq-inquiry-header__copy">
              <h2 class="bq-inquiry-header__name">{{ query.name }}</h2>
              <p class="bq-inquiry-header__org">{{ organizationLabel }}</p>
            </div>
          </div>
          <button
            type="button"
            class="bq-inquiry-header__close"
            aria-label="Close inquiry drawer"
            @click="close"
          >
            <v-icon icon="mdi-close" size="20" />
          </button>
        </header>

        <div class="bq-inquiry-meta">
          <div class="bq-inquiry-meta__tags">
            <span class="bq-inquiry-tag">{{ query.statusLabel }}</span>
            <span
              v-if="query.leadScore != null"
              class="bq-inquiry-tag bq-inquiry-tag--score"
              :class="`bq-inquiry-tag--${scoreTone}`"
              title="AI lead score (0–100)"
            >
              Score {{ query.leadScore }}
            </span>
          </div>
          <time class="bq-inquiry-meta__time">{{ query.createdOnLabel }}</time>
        </div>

        <div class="bq-inquiry-body">
          <section
            v-if="query.leadOverview || query.bestTimeToCallLabel"
            class="bq-inquiry-card"
            aria-labelledby="bq-ai-overview-heading"
          >
            <div class="bq-inquiry-card__head">
              <span class="bq-inquiry-card__icon bq-inquiry-card__icon--summary" aria-hidden="true">
                <v-icon icon="mdi-robot-outline" size="18" />
              </span>
              <h3 id="bq-ai-overview-heading" class="bq-inquiry-card__title">AI overview</h3>
            </div>
            <div v-if="query.leadOverview" class="bq-inquiry-message">
              <p>{{ query.leadOverview }}</p>
            </div>
            <dl v-if="query.bestTimeToCallLabel" class="bq-inquiry-grid bq-inquiry-grid--compact">
              <div class="bq-inquiry-grid__item bq-inquiry-grid__item--full">
                <dt>Best time to call</dt>
                <dd>{{ query.bestTimeToCallLabel }}</dd>
              </div>
            </dl>
          </section>

          <section class="bq-inquiry-card" aria-labelledby="bq-lead-profile-heading">
            <div class="bq-inquiry-card__head">
              <span class="bq-inquiry-card__icon bq-inquiry-card__icon--profile" aria-hidden="true">
                <v-icon icon="mdi-account-outline" size="18" />
              </span>
              <h3 id="bq-lead-profile-heading" class="bq-inquiry-card__title">Lead profile</h3>
            </div>

            <dl class="bq-inquiry-grid">
              <div class="bq-inquiry-grid__item">
                <dt>Name</dt>
                <dd>{{ query.name }}</dd>
              </div>
              <div class="bq-inquiry-grid__item">
                <dt>Organization</dt>
                <dd>{{ organizationLabel }}</dd>
              </div>
              <div class="bq-inquiry-grid__item">
                <dt>Email</dt>
                <dd>{{ emailLabel }}</dd>
              </div>
              <div class="bq-inquiry-grid__item">
                <dt>Phone</dt>
                <dd>{{ phoneLabel }}</dd>
              </div>
              <div class="bq-inquiry-grid__item bq-inquiry-grid__item--full">
                <dt>Status</dt>
                <dd>
                  <span
                    class="bq-status"
                    :class="`bq-status--${query.statusTone || 'neutral'}`"
                  >
                    {{ query.statusLabel }}
                  </span>
                </dd>
              </div>
            </dl>
          </section>

          <section
            v-if="customFieldEntries.length"
            class="bq-inquiry-card"
            aria-labelledby="bq-custom-fields-heading"
          >
            <div class="bq-inquiry-card__head">
              <span class="bq-inquiry-card__icon bq-inquiry-card__icon--profile" aria-hidden="true">
                <v-icon icon="mdi-form-textbox" size="18" />
              </span>
              <h3 id="bq-custom-fields-heading" class="bq-inquiry-card__title">Custom fields</h3>
            </div>

            <dl class="bq-inquiry-grid bq-inquiry-grid--compact">
              <div
                v-for="entry in customFieldEntries"
                :key="entry.name"
                class="bq-inquiry-grid__item"
              >
                <dt>{{ entry.label }}</dt>
                <dd>{{ entry.value }}</dd>
              </div>
            </dl>
          </section>

          <section class="bq-inquiry-card" aria-labelledby="bq-inquiry-details-heading">
            <div class="bq-inquiry-card__head">
              <span class="bq-inquiry-card__icon bq-inquiry-card__icon--details" aria-hidden="true">
                <v-icon icon="mdi-file-document-outline" size="18" />
              </span>
              <h3 id="bq-inquiry-details-heading" class="bq-inquiry-card__title">Inquiry details</h3>
            </div>

            <dl class="bq-inquiry-grid bq-inquiry-grid--compact">
              <div class="bq-inquiry-grid__item">
                <dt>Type</dt>
                <dd>{{ inquiryType }}</dd>
              </div>
              <div class="bq-inquiry-grid__item">
                <dt>Submitted</dt>
                <dd>{{ query.createdOnLabel }}</dd>
              </div>
            </dl>

            <div class="bq-inquiry-message">
              <p>{{ inquiryText || 'No inquiry message provided.' }}</p>
            </div>
          </section>

          <section
            class="bq-inquiry-card"
            aria-labelledby="bq-call-history-heading"
          >
            <div class="bq-inquiry-card__head">
              <span class="bq-inquiry-card__icon bq-inquiry-card__icon--details" aria-hidden="true">
                <v-icon icon="mdi-phone-log-outline" size="18" />
              </span>
              <h3 id="bq-call-history-heading" class="bq-inquiry-card__title">
                Call history
                <template v-if="callHistory.length">({{ callHistory.length }})</template>
              </h3>
            </div>

            <p v-if="callHistoryLoading" class="bq-call-history__empty">
              Loading calls…
            </p>
            <p v-else-if="!callHistory.length" class="bq-call-history__empty">
              No linked calls yet.
            </p>
            <div v-else class="bq-call-history">
              <details
                v-for="call in callHistory"
                :key="call.id"
                class="bq-call-history__item"
              >
                <summary class="bq-call-history__summary">
                  <span class="bq-call-history__date">{{ call.calledOnLabel }}</span>
                  <span class="bq-call-history__meta">
                    <span v-if="call.directionLabel && call.directionLabel !== '—'" class="bq-call-history__status">
                      {{ call.directionLabel }}
                    </span>
                    <span
                      v-if="call.status"
                      class="bq-call-history__status"
                    >{{ call.status }}</span>
                    <span v-if="call.durationLabel">{{ call.durationLabel }}</span>
                  </span>
                </summary>
                <div v-if="call.recordingUrl" class="bq-call-history__recording">
                  <audio
                    controls
                    preload="none"
                    :src="call.recordingUrl"
                    class="bq-call-history__audio"
                  >
                    Your browser does not support audio playback.
                  </audio>
                </div>
                <ul v-if="call.transcript.length" class="bq-call-history__transcript">
                  <li v-for="(line, lineIndex) in call.transcript" :key="lineIndex">
                    {{ line }}
                  </li>
                </ul>
                <p v-else class="bq-call-history__empty">No transcript available.</p>
              </details>
            </div>
          </section>

          <section
            v-if="historyLoading || statusHistory.length"
            class="bq-inquiry-card"
            aria-labelledby="bq-status-history-heading"
          >
            <div class="bq-inquiry-card__head">
              <span class="bq-inquiry-card__icon bq-inquiry-card__icon--details" aria-hidden="true">
                <v-icon icon="mdi-timeline-clock-outline" size="18" />
              </span>
              <h3 id="bq-status-history-heading" class="bq-inquiry-card__title">
                Activity timeline
                <template v-if="statusHistory.length">({{ statusHistory.length }})</template>
              </h3>
            </div>

            <p v-if="historyLoading" class="bq-call-history__empty">
              Loading history…
            </p>
            <ul v-else class="bq-status-history">
              <li
                v-for="entry in statusHistory"
                :key="entry.id"
                class="bq-status-history__item"
              >
                <div class="bq-status-history__row">
                  <span
                    class="bq-status"
                    :class="`bq-status--${entry.tone}`"
                  >{{ entry.statusLabel }}</span>
                  <time class="bq-status-history__time">{{ entry.createdOnLabel }}</time>
                </div>
                <p v-if="entry.comments" class="bq-status-history__comment">
                  {{ entry.comments }}
                </p>
                <span v-if="entry.performedBy" class="bq-status-history__by">
                  by {{ entry.performedBy }}
                </span>
              </li>
            </ul>
          </section>

          <section
            v-if="summaryBullets.length"
            class="bq-inquiry-card"
            aria-labelledby="bq-inquiry-summary-heading"
          >
            <div class="bq-inquiry-card__head">
              <span class="bq-inquiry-card__icon bq-inquiry-card__icon--summary" aria-hidden="true">
                <v-icon icon="mdi-brain" size="18" />
              </span>
              <h3 id="bq-inquiry-summary-heading" class="bq-inquiry-card__title">Inquiry summary</h3>
            </div>

            <ul class="bq-inquiry-summary">
              <li v-for="(bullet, index) in summaryBullets" :key="index">{{ bullet }}</li>
            </ul>
          </section>

          <section class="bq-inquiry-card" aria-labelledby="bq-suggested-actions-heading">
            <div class="bq-inquiry-card__head">
              <span class="bq-inquiry-card__icon bq-inquiry-card__icon--actions" aria-hidden="true">
                <v-icon icon="mdi-lightning-bolt-outline" size="18" />
              </span>
              <h3 id="bq-suggested-actions-heading" class="bq-inquiry-card__title">Suggested actions</h3>
            </div>

            <div class="bq-inquiry-actions">
              <button
                type="button"
                class="bq-inquiry-action"
                :disabled="!hasEmail"
                @click="$emit('contact-email', query)"
              >
                <span class="bq-inquiry-action__icon" aria-hidden="true">
                  <v-icon icon="mdi-email-outline" size="18" />
                </span>
                <span class="bq-inquiry-action__text">Send email</span>
              </button>

              <button
                type="button"
                class="bq-inquiry-action"
                :disabled="!hasPhone"
                @click="$emit('call-lead', query)"
              >
                <span class="bq-inquiry-action__icon" aria-hidden="true">
                  <v-icon icon="mdi-phone-outline" size="18" />
                </span>
                <span class="bq-inquiry-action__text">Call lead</span>
              </button>

              <button
                type="button"
                class="bq-inquiry-action"
                :disabled="!query.requestId || currentStatus === 'CONVERTED'"
                @click="$emit('mark-converted', query)"
              >
                <span class="bq-inquiry-action__icon" aria-hidden="true">
                  <v-icon icon="mdi-check-circle-outline" size="18" />
                </span>
                <span class="bq-inquiry-action__text">Mark as converted</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </v-navigation-drawer>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import { useDisplay } from 'vuetify'
import { getLeadHistory, getLeadCallHistory } from '@/api/leadsApi'
import { isApiSuccess } from '@/mixins/Extras/ApiResponse'
import {
  businessQueryInitials,
  businessQueryInquirySummaryBullets,
  businessQueryInquiryText,
  businessQueryInquiryType,
  normalizeBusinessQueryStatus,
  businessQueryStatusLabel,
  businessQueryStatusTone,
  leadScoreTone,
} from '@/utils/businessQueries'
import { formatDateTime, formatPhoneNumber } from '@/utils/format'
import {
  formatEpochAsDdMmYyyy,
} from '@/utils/leadFieldTypes'
import { normalizeLeadCallHistoryList } from '@/utils/leadCallHistory'

defineOptions({ name: 'BusinessQueryInquiryDrawer' })

const DRAWER_OPEN_CLASS = 'bq-inquiry-drawer-open'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  query: { type: Object, default: null },
  /** Optional campaign scope for getLeadCallHistory. */
  taskId: { type: String, default: '' },
})

const emit = defineEmits([
  'update:modelValue',
  'contact-email',
  'call-lead',
  'mark-converted',
])

const display = useDisplay()

const drawerWidth = computed(() =>
  display.mobile.value ? '100%' : Math.min(520, display.width.value),
)

const initials = computed(() => businessQueryInitials(props.query?.name))
const organizationLabel = computed(() => {
  const company = String(props.query?.company || '').trim()
  return company || '—'
})
const emailLabel = computed(() => {
  const email = String(props.query?.email || '').trim()
  return email && email !== '—' ? email : '—'
})
const phoneLabel = computed(() => {
  const phone = String(props.query?.phone || '').trim()
  return phone ? formatPhoneNumber(phone) : '—'
})
const hasEmail = computed(() => emailLabel.value !== '—')
const hasPhone = computed(() => phoneLabel.value !== '—')
const inquiryText = computed(() => businessQueryInquiryText(props.query))
const inquiryType = computed(() => businessQueryInquiryType(props.query))
const summaryBullets = computed(() => businessQueryInquirySummaryBullets(props.query))
const currentStatus = computed(() => normalizeBusinessQueryStatus(props.query?.status))
const scoreTone = computed(() => leadScoreTone(props.query?.leadScore) || 'neutral')

const customFieldEntries = computed(() => {
  const fields = props.query?.customFields
  if (!fields || typeof fields !== 'object') return []

  return Object.entries(fields)
    .filter(([, value]) => value != null && String(value).trim() !== '')
    .map(([name, value]) => {
      let display = String(value)
      const key = String(name)
      if (/date|dob|birthday|expiry|expire/i.test(key)) {
        const formatted = formatEpochAsDdMmYyyy(value)
        if (formatted) display = formatted
      } else if (/phone|mobile|whatsapp|contact_number/i.test(key)) {
        const phone = formatPhoneNumber(value, { emptyLabel: '' })
        if (phone) display = phone
      }
      return {
        name,
        label: key.replace(/_/g, ' '),
        value: display,
      }
    })
})

const callHistory = ref([])
const callHistoryLoading = ref(false)

// Status-change audit trail — only queryable via getLeadHistory.
const statusHistory = ref([])
const historyLoading = ref(false)

async function loadCallHistory() {
  const requestId = String(props.query?.requestId || '').trim()
  callHistory.value = []
  if (!requestId) return

  callHistoryLoading.value = true
  try {
    const payload = { request_id: requestId }
    const taskId = String(props.taskId || '').trim()
    if (taskId) payload.task_id = taskId

    const response = await getLeadCallHistory(payload)
    if (!isApiSuccess(response)) return
    callHistory.value = normalizeLeadCallHistoryList(response)
  } finally {
    callHistoryLoading.value = false
  }
}

async function loadStatusHistory() {
  const requestId = String(props.query?.requestId || '').trim()
  statusHistory.value = []
  if (!requestId) return

  historyLoading.value = true
  try {
    const response = await getLeadHistory({ request_id: requestId })
    if (!isApiSuccess(response)) return

    const items = Array.isArray(response?.data?.items)
      ? response.data.items
      : Array.isArray(response?.items)
        ? response.items
        : []

    statusHistory.value = items.map((item, index) => {
      const status = normalizeBusinessQueryStatus(item?.request_status)
      return {
        id: item?.history_id || `history-${index}`,
        statusLabel: businessQueryStatusLabel(status),
        tone: businessQueryStatusTone(status) || 'neutral',
        comments: String(item?.comments ?? '').trim(),
        performedBy: String(item?.performed_by ?? '').trim(),
        createdOnLabel: item?.created_on ? formatDateTime(item.created_on) : '',
      }
    })
  } finally {
    historyLoading.value = false
  }
}

watch(
  () => [props.modelValue, props.query?.requestId, props.taskId],
  ([open]) => {
    if (open) {
      void loadStatusHistory()
      void loadCallHistory()
    }
  },
)

function setDrawerOpenState(open) {
  const root = document.documentElement
  if (!root) return
  if (open) root.classList.add(DRAWER_OPEN_CLASS)
  else root.classList.remove(DRAWER_OPEN_CLASS)
}

function close() {
  emit('update:modelValue', false)
}

function onDrawerUpdate(value) {
  emit('update:modelValue', value)
}

watch(
  () => props.modelValue,
  (open) => setDrawerOpenState(open),
  { immediate: true },
)

onBeforeUnmount(() => setDrawerOpenState(false))
</script>
